import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { comandarFase, obterAndamento } from '../api/andamento';
import type { AndamentoDaTurma, EstadoEtapa, FaseDaTurma, GrupoNoQuadro } from '../api/andamento';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

const INTERVALO_MS = 10000;
const FOCAVEIS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const ROTULO_ESTADO: Record<EstadoEtapa, string> = {
    nao_aberta: 'não aberta',
    aguardando: 'aguardando',
    jogando: 'jogando',
    enviado: 'enviado',
    apurado: 'apurado',
    encerrada_sem_entrega: 'sem entrega',
};

interface ConteudoProps {
    grupo: GrupoNoQuadro;
    fases: FaseDaTurma[];
}

/** Mesmo conteúdo no quadrinho e na carta: uma fonte só, dois tamanhos. */
function ConteudoDoGrupo({ grupo, fases }: ConteudoProps) {
    return (
        <>
            <header>
                <h3>{grupo.grupo_id}</h3>
                {grupo.salas.length ? (
                    <p className="salas-candidatas">
                        {grupo.salas.map((sala) => (
                            <span key={sala} className="sala-atual">{sala}</span>
                        ))}
                    </p>
                ) : (
                    <span className="sem-tablet">Fora de sala</span>
                )}
            </header>
            {grupo.conflito && (
                <p className="aviso-conflito" role="alert">
                    Duas fases abertas: <strong>{grupo.conflito.fase_que_abriu_depois}</strong>{' '}
                    abriu depois de {grupo.conflito.fase_anterior}, e o grupo joga uma fase por vez.
                    Confirme com o facilitador em qual sala o grupo está de fato e encerre a outra
                    fase para a turma.
                </p>
            )}
            <p className="jogadores-do-grupo">
                <strong>{grupo.online} de {grupo.participantes}</strong> jogadores
            </p>
            <ul className="etapas-do-grupo">
                {fases.map((fase) => {
                    const etapa = grupo.etapas[fase.fase];
                    if (!etapa) return null;
                    return (
                        <li key={fase.fase}>
                            <span className="nome-da-fase">{fase.rotulo}</span>
                            <span className={`chip-etapa chip-${etapa.estado}`}>
                                {ROTULO_ESTADO[etapa.estado]}
                            </span>
                            <span className="pontos">
                                {etapa.pontuacao !== null ? `${Number(etapa.pontuacao)} pts` : '-'}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

interface CartaProps {
    grupo: GrupoNoQuadro;
    fases: FaseDaTurma[];
    aoFechar: () => void;
    aoVerJogadores?: () => void;
}

/** Carta do grupo: a mesma informação, no centro da tela. */
function CartaDoGrupo({ grupo, fases, aoFechar, aoVerJogadores }: CartaProps) {
    const carta = useRef<HTMLDivElement>(null);

    useEffect(() => {
        carta.current?.focus();
        const noTeclado = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') {
                aoFechar();
                return;
            }
            if (evento.key !== 'Tab' || !carta.current) return;
            // Prende o foco: sem isto o Tab sai da carta e vai mexer na tela de baixo,
            // que continua lá embaixo do fundo escuro.
            const focaveis = [...carta.current.querySelectorAll<HTMLElement>(FOCAVEIS)].filter(
                (elemento) => !elemento.hasAttribute('disabled'),
            );
            const primeiro = focaveis[0] ?? carta.current;
            const ultimo = focaveis.at(-1) ?? carta.current;
            const atual = document.activeElement;
            if (evento.shiftKey && (atual === primeiro || atual === carta.current)) {
                evento.preventDefault();
                ultimo.focus();
            } else if (!evento.shiftKey && atual === ultimo) {
                evento.preventDefault();
                primeiro.focus();
            }
        };
        document.addEventListener('keydown', noTeclado);
        return () => document.removeEventListener('keydown', noTeclado);
    }, [aoFechar]);

    const classes = [
        'carta-grupo',
        grupo.salas.length ? 'em-sala' : '',
        grupo.conflito ? 'em-conflito' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className="fundo-da-carta" data-testid="fundo-da-carta" onClick={aoFechar}>
            <div
                ref={carta}
                className={classes}
                role="dialog"
                aria-modal="true"
                aria-label={`Grupo ${grupo.grupo_id}`}
                tabIndex={-1}
                onClick={(evento) => evento.stopPropagation()}
            >
                <button type="button" className="fechar-carta" onClick={aoFechar} aria-label="Fechar">
                    ×
                </button>
                <ConteudoDoGrupo grupo={grupo} fases={fases} />
                {aoVerJogadores && (
                    <button type="button" className="botao-entrar ver-jogadores" onClick={aoVerJogadores}>
                        Ver jogadores
                    </button>
                )}
            </div>
        </div>
    );
}

/** O desfecho do último comando: feito, recusado ou — o que mais importa — indeterminado. */
type Desfecho =
    | { tipo: 'feito'; texto: string }
    | { tipo: 'recusado'; texto: string }
    | { tipo: 'indeterminado'; texto: string };

function desfechoDoErro(erro: unknown): Desfecho {
    const { status, corpo } = erro as { status?: number; corpo?: Record<string, unknown> | null };
    const mensagem = typeof corpo?.mensagem === 'string' ? corpo.mensagem : null;
    const motivo = typeof corpo?.motivo === 'string' ? corpo.motivo : null;
    if (status === 504) {
        // Não respondeu não é o mesmo que não valeu: dizer "falhou" aqui faria o
        // operador repetir, no meio do evento, um comando que já pode ter valido.
        return { tipo: 'indeterminado', texto: mensagem ?? 'A operação do jogo não respondeu a tempo. O comando pode ter valido: confira o quadro antes de repetir.' };
    }
    if (status === 409 || status === 404) {
        return { tipo: 'recusado', texto: motivo ? `Recusado: ${motivo}` : (mensagem ?? 'Comando recusado.') };
    }
    return { tipo: 'recusado', texto: mensagem ?? 'Não foi possível enviar o comando.' };
}

interface ComandosProps {
    grupo: GrupoNoQuadro;
    desfecho: Desfecho | null;
    ocupado: boolean;
    aoComandar: (fase: string, rotulo: string, acao: 'abrir' | 'encerrar') => void;
}

/** O que este grupo aceita agora. Quem decide é o servidor; aqui só se mostra. */
function ComandosDoGrupo({ grupo, desfecho, ocupado, aoComandar }: ComandosProps) {
    // O grupo joga uma fase por vez: enquanto está numa, a única coisa que cabe
    // é encerrá-la. Oferecer as outras, ainda que apagadas, faria a tela
    // contradizer a regra — some o que não cabe.
    const comandos = (grupo.comandos ?? []).filter(
        (comando) => comando.pode_abrir || comando.pode_encerrar,
    );
    if (!comandos.length) return null;
    return (
        <div className="comandos-do-grupo" role="group" aria-label={`Fases do ${grupo.grupo_id}`}>
            {comandos.map((comando) => {
                const acao = comando.pode_encerrar ? 'encerrar' : 'abrir';
                return (
                    <button
                        key={comando.fase}
                        type="button"
                        className={`comando-${acao}`}
                        disabled={ocupado}
                        onClick={() => aoComandar(comando.fase, comando.rotulo, acao)}
                    >
                        {acao === 'abrir' ? 'Abrir' : 'Encerrar'} {comando.rotulo}
                    </button>
                );
            })}
            {desfecho && (
                <p
                    className={`desfecho-comando desfecho-${desfecho.tipo}`}
                    data-desfecho={desfecho.tipo}
                    role={desfecho.tipo === 'feito' ? 'status' : 'alert'}
                >
                    {desfecho.texto}
                </p>
            )}
        </div>
    );
}

interface PainelProps {
    /** Leva para a aba Jogadores já filtrada pela turma e pelo grupo da carta. */
    aoVerJogadores?: (turmaId: string, grupoId: string) => void;
    csrfToken?: string;
}

export function AndamentoPainel({ aoVerJogadores, csrfToken }: PainelProps = {}) {
    const [comandando, setComandando] = useState<string | null>(null);
    // O desfecho de um grupo aparece no card dele, e não numa barra única: no
    // meio do evento, saber *qual grupo* é metade da informação.
    const [desfechoDoGrupo, setDesfechoDoGrupo] = useState<Record<string, Desfecho>>({});
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [quadro, setQuadro] = useState<AndamentoDaTurma | null>(null);
    const [erro, setErro] = useState(false);
    const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
    // Filtro da tela, não da consulta: a fase escolhida foca os quadrinhos nela,
    // e o estado reduz quais grupos aparecem.
    const [faseEscolhida, setFaseEscolhida] = useState<string | null>(null);
    const [estadoEscolhido, setEstadoEscolhido] = useState<EstadoEtapa | null>(null);
    const [grupoAberto, setGrupoAberto] = useState<string | null>(null);
    const [espacoDoTitulo, setEspacoDoTitulo] = useState<HTMLElement | null>(null);
    // Fechar a carta devolve o foco ao quadrinho que a abriu.
    const quadrinhos = useRef(new Map<string, HTMLButtonElement | null>());

    const carregar = useCallback(async (alvo: string) => {
        try {
            setQuadro(await obterAndamento(alvo));
            setErro(false);
        } catch {
            setErro(true);
        }
        setAtualizadoEm(new Date());
    }, []);

    const comandarGrupo = async (
        grupoId: string,
        fase: string,
        rotulo: string,
        acao: 'abrir' | 'encerrar',
    ) => {
        if (!turmaId) return;
        const pergunta =
            acao === 'abrir'
                ? `Abrir ${rotulo} para o ${grupoId} da turma ${turmaId}?`
                : `Encerrar ${rotulo} do ${grupoId}? Quem não entregou deixa de poder entregar.`;
        if (!window.confirm(pergunta)) return;

        setDesfechoDoGrupo((atual) => {
            const proximo = { ...atual };
            delete proximo[grupoId];
            return proximo;
        });
        setComandando(`${grupoId}:${fase}`);
        try {
            const saida = await comandarFase(fase, turmaId, acao, csrfToken ?? '', grupoId);
            setDesfechoDoGrupo((atual) => ({
                ...atual,
                [grupoId]: {
                    tipo: 'feito',
                    texto: `${rotulo} ${saida.status} — ${saida.tablets} tablets avisados.`,
                },
            }));
            await carregar(turmaId);
        } catch (erro) {
            setDesfechoDoGrupo((atual) => ({ ...atual, [grupoId]: desfechoDoErro(erro) }));
        } finally {
            setComandando(null);
        }
    };

    useEffect(() => {
        setEspacoDoTitulo(document.getElementById('acoes-da-secao'));
    }, []);

    useEffect(() => {
        listarTurmas()
            .then((lista) => {
                setTurmas(lista);
                setTurmaId((atual) => manterSeDisponivel(atual, lista.map((turma) => turma.id), lista[0]?.id ?? null));
            })
            .catch(() => setErro(true));
    }, []);

    useEffect(() => {
        if (turmaId) void carregar(turmaId);
    }, [turmaId, carregar]);

    useEffect(() => {
        // Sempre ligado: a tela é de acompanhamento, e um painel parado sem
        // aviso é pior do que um dado de 10s atrás.
        if (!turmaId) return undefined;
        const relogio = window.setInterval(() => void carregar(turmaId), INTERVALO_MS);
        return () => window.clearInterval(relogio);
    }, [turmaId, carregar]);

    function escolherFase(fase: string) {
        setFaseEscolhida((atual) => (atual === fase ? null : fase));
        setEstadoEscolhido(null);
    }

    const resumo = quadro?.resumo;
    const fasesVisiveis = quadro
        ? quadro.fases.filter((fase) => !faseEscolhida || fase.fase === faseEscolhida)
        : [];
    const contagem = new Map<EstadoEtapa, number>();
    if (quadro && faseEscolhida) {
        for (const grupo of quadro.grupos) {
            const etapa = grupo.etapas[faseEscolhida];
            if (etapa) contagem.set(etapa.estado, (contagem.get(etapa.estado) ?? 0) + 1);
        }
    }
    const gruposVisiveis = (quadro?.grupos ?? []).filter((grupo) => {
        if (!faseEscolhida || !estadoEscolhido) return true;
        return grupo.etapas[faseEscolhida]?.estado === estadoEscolhido;
    });

    const aberto = gruposVisiveis.find((grupo) => grupo.grupo_id === grupoAberto) ?? null;

    const controles = (
        <div className="controles-andamento">
            <label className="campo-turma">
                Turma
                <select value={turmaId ?? ''} onChange={(e) => setTurmaId(e.target.value)}>
                    {turmas.map((turma) => (
                        <option key={turma.id} value={turma.id}>{turma.id}</option>
                    ))}
                </select>
            </label>
            <p className="atualizacao" role="status">
                {`atualiza a cada 10s${atualizadoEm ? ` · ${atualizadoEm.toLocaleTimeString('pt-BR')}` : ''}`}
            </p>
        </div>
    );

    return (
        <div className="andamento">
            {/* O título da seção é da página das abas; o seletor vai para a mesma linha
                quando esse espaço existe, e fica aqui quando o painel roda sozinho. */}
            {espacoDoTitulo ? createPortal(controles, espacoDoTitulo) : controles}

            {erro && <p className="estado-variaveis mensagem-login">Não foi possível carregar o andamento.</p>}

            {resumo && (
                <div className="cartoes-andamento">
                    <article><strong>{resumo.jogando} de {resumo.grupos}</strong><span>grupos jogando</span></article>
                    <article><strong>{resumo.online} de {resumo.participantes}</strong><span>participantes conectados</span></article>
                </div>
            )}

            {quadro && quadro.fases.length === 0 && (
                <p className="estado-variaveis">
                    Nenhuma fase foi aberta nesta turma ainda. O quadro aparece quando o facilitador abrir a primeira.
                </p>
            )}

            {quadro && quadro.fases.length > 0 && (
                <ul className="faixa-fases" aria-label="Fases da turma">
                    {quadro.fases.map((fase) => (
                        <li key={fase.fase}>
                            <button
                                type="button"
                                className={`fase-${fase.status}`}
                                aria-pressed={faseEscolhida === fase.fase}
                                onClick={() => escolherFase(fase.fase)}
                            >
                                <strong>{fase.rotulo}</strong>
                                <span>
                                    {fase.status === 'aberta' ? 'aberta' : fase.status === 'encerrada' ? 'encerrada' : 'não aberta'}
                                    {' · '}
                                    {fase.entregues}/{fase.total} entregaram
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {faseEscolhida && (
                <div className="filtros-estado" role="group" aria-label="Estados nesta fase">
                    {[...contagem.entries()].map(([estado, total]) => (
                        <button
                            key={estado}
                            type="button"
                            className={`chip-etapa chip-${estado}`}
                            aria-pressed={estadoEscolhido === estado}
                            onClick={() => setEstadoEscolhido((atual) => (atual === estado ? null : estado))}
                        >
                            {ROTULO_ESTADO[estado]} ({total})
                        </button>
                    ))}
                    <button type="button" className="botao-texto" onClick={() => escolherFase(faseEscolhida)}>
                        ver todas as fases
                    </button>
                </div>
            )}

            {quadro && gruposVisiveis.length === 0 && quadro.grupos.length > 0 && (
                <p className="estado-variaveis">Nenhum grupo neste estado agora.</p>
            )}

            {quadro && gruposVisiveis.length > 0 && (
                <ul className="grade-grupos" aria-label="Grupos">
                    {gruposVisiveis.map((grupo) => (
                        <li
                            key={grupo.grupo_id}
                            aria-label={`Grupo ${grupo.grupo_id}`}
                            className={[
                                'quadrinho',
                                grupo.salas.length ? 'em-sala' : '',
                                grupo.conflito ? 'em-conflito' : '',
                            ].filter(Boolean).join(' ')}
                        >
                            {/* O card virou caixa para poder conter os comandos: botão
                                dentro de botão não existe em HTML. O conteúdo segue
                                clicável e continua abrindo a carta. */}
                            <button
                                type="button"
                                className="conteudo-do-quadrinho"
                                aria-label={`Grupo ${grupo.grupo_id}`}
                                aria-haspopup="dialog"
                                ref={(elemento) => { quadrinhos.current.set(grupo.grupo_id, elemento); }}
                                onClick={() => setGrupoAberto(grupo.grupo_id)}
                            >
                                <ConteudoDoGrupo grupo={grupo} fases={fasesVisiveis} />
                            </button>
                            <ComandosDoGrupo
                                grupo={grupo}
                                desfecho={desfechoDoGrupo[grupo.grupo_id] ?? null}
                                ocupado={comandando !== null}
                                aoComandar={(fase, rotulo, acao) =>
                                    void comandarGrupo(grupo.grupo_id, fase, rotulo, acao)
                                }
                            />
                        </li>
                    ))}
                </ul>
            )}

            {aberto && (
                <CartaDoGrupo
                    grupo={aberto}
                    fases={fasesVisiveis}
                    aoFechar={() => {
                        const quadrinho = quadrinhos.current.get(aberto.grupo_id);
                        setGrupoAberto(null);
                        quadrinho?.focus();
                    }}
                    aoVerJogadores={
                        aoVerJogadores && turmaId
                            ? () => {
                                  setGrupoAberto(null);
                                  aoVerJogadores(turmaId, aberto.grupo_id);
                              }
                            : undefined
                    }
                />
            )}
        </div>
    );
}
